import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketDetail, TicketKarte } from "@kundeninfo/api-client";
import { useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import { Objektsymbol } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { ObjektVerweis } from "../../components/objektPanel/ObjektVerweis.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { ticketReceipt } from "./receipts.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DIE ZEILE „TEIL VON" (Spec Teilaufgaben R23, E441; Öffnen statt Springen K8): Das Elternticket ist ein fremdes Objekt —
 * Objektsymbol Ticket, Anzeige-Nummer und Titel als Text ohne Chip-Fläche, der Verweis direkt dahinter (Panelknopf: der Weg
 * des Dialogs, `onToParent` — seitlich bzw. Schließen, wenn es darunter liegt, F14; Sprungknopf: `/tickets/<id>`), „Lösen"
 * (leise, gestapelte Bestätigung) am Zeilenende. Das Sofort-Popup nennt echte Nummer und Eigentümer des Elterntickets.
 */
export function ParentLine(p: {
  parent: NonNullable<TicketDetail["parent"]>; child: { id: string; displayNumber: string; version: number };
  client: TicketsClient; disabled: boolean; onToParent: () => void; onDetached: (k: TicketKarte) => void;
  onError: (text: string) => void;
}): ReactElement {
  const { bestaetigen } = useBestaetigung();
  const e = p.parent;
  const eigentuemer = e.owner.kind === "USER" ? e.owner.user?.name ?? "" : e.owner.kind === "TEAM"
    ? e.owner.team?.shortCode ?? "" : HIERARCHY_TEXT.system;
  const popup = useSofortPopup<HTMLSpanElement>(`${HIERARCHY_TEXT.realNumber(e.ticketNumber)} · ${HIERARCHY_TEXT.ownerOf(eigentuemer)}`);
  async function loesen(): Promise<void> {
    if (!(await bestaetigen({ titel: HIERARCHY_TEXT.detachConfirmTitle(p.child.displayNumber, e.displayNumber),
      text: HIERARCHY_TEXT.detachConfirmText, bestaetigenText: HIERARCHY_TEXT.detach }))) return;
    try {
      const k = await p.client.updateTicket(p.child.id, p.child.version, { parentTicketId: null });
      ticketReceipt({ text: HIERARCHY_TEXT.receiptDetached(k.ticketNumber) });
      p.onDetached(k);
    } catch (f) {
      p.onError(f instanceof ApiError ? (f.fields?.[0]?.message ?? f.message) : HIERARCHY_TEXT.failChange);
    }
  }
  return (
    <span className="td__teilvon td__nennung">
      <Objektsymbol art="ticket" groesse={16} />
      <span ref={popup} className="td__teilvontext">{`${e.displayNumber} ${e.title}`}</span>
      <ObjektVerweis bezug={{ art: "ticket", id: e.id, label: `${e.displayNumber} ${e.title}` }} eigenesPanel={p.onToParent} />
      <KiKnopf art="leise" kompakt deaktiviert={p.disabled} onClick={() => { void loesen(); }}>{HIERARCHY_TEXT.detach}</KiKnopf>
    </span>
  );
}
